import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {CompanyModel} from '../../models/api/companyModel';
import {ApiRuleBundles, RuleBundleModelStrict} from '../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../models/api/ruleCommonTypes';
import {RuleBaseModelStrict} from '../../models/api/ruleModel';
import {patchApiAsync, patchAsync} from '../../services/http/http';
import {ListMutation} from '../helpers/listMutation';
import {ResourceTableCommonPageType} from '../helpers/resourceListPagination/resourceListPaginationHelpers';
import {buildRuleBundleBulkUrl} from '../rules/list/ruleListHelpers';

export enum RuleBundleChangeActiveStatusActionsEnum {
  TOGGLE_ACTIVE = 'TOGGLE_ACTIVE',
  SET_ACTIVE = 'SET_ACTIVE',
  SET_INACTIVE = 'SET_INACTIVE',
}

export enum RuleBundleMutationsEnum {
  CHANGE_ACTIVE_STATUS = 'CHANGE_ACTIVE_STATUS',
  REORDER = 'REORDER',
  DELETE = 'DELETE',
}

export type RuleListData = ResourceTableCommonPageType<RuleBundleModelStrict>;
interface RuleBundleMutationPayload {
  bundles: ReadonlyArray<RuleBundleModelStrict>;
  company: CompanyModel;
  namespace: string;
}

interface ChangeActiveStatusMutationAction {
  type: RuleBundleMutationsEnum.CHANGE_ACTIVE_STATUS;
  payload: RuleBundleMutationPayload & {
    action: RuleBundleChangeActiveStatusActionsEnum;
  };
}

interface DeleteMutationAction {
  type: RuleBundleMutationsEnum.DELETE;
  payload: RuleBundleMutationPayload;
}

interface ReorderMutationAction {
  type: RuleBundleMutationsEnum.REORDER;
  payload: {
    bundle: RuleBundleModelStrict;
    previousOrder: number;
    newOrder: number;
    company: CompanyModel;
  };
}

export type RuleListMutationAction =
  | ChangeActiveStatusMutationAction
  | DeleteMutationAction
  | ReorderMutationAction;

const changeActiveStatusMutation: ListMutation<RuleListData, ChangeActiveStatusMutationAction, void> = {
  mutationKey: RuleBundleMutationsEnum.CHANGE_ACTIVE_STATUS,

  async runSideEffect(payload) {
    const {bundles, company, namespace, action} = payload;
    const url = buildRuleBundleBulkUrl(company, namespace);
    const selectedIds = bundles
      // TODO @required-tagging: Allow disable/enable.
      .filter((bundle) => bundle.type !== RuleBundleTypesEnum.REQUIRED_TAGGING)
      .map((bundle) => bundle.id);

    return patchAsync(url, {selected_ids: selectedIds, action});
  },

  runOptimisticUpdate(data, payload) {
    const changeActiveStateFunction = getChangeActiveStateFunction(payload.action);
    const bundleIds = payload.bundles.map((bundle) => bundle.id);

    return {
      ...data,
      items: data.items.map((bundle) => {
        if (
          !bundle.id ||
          !bundleIds.includes(bundle.id) ||
          bundle.type === RuleBundleTypesEnum.REQUIRED_TAGGING
        ) {
          return bundle;
        }

        return {
          ...bundle,
          rules: bundle.rules.map(changeActiveStateFunction),
        };
      }),
    };
  },
};

const deleteMutation: ListMutation<RuleListData, DeleteMutationAction, void> = {
  mutationKey: RuleBundleMutationsEnum.DELETE,

  async runSideEffect(payload) {
    const {bundles, company, namespace} = payload;
    const ids = bundles.map((bundle) => bundle.id);
    const url = buildRuleBundleBulkUrl(company, namespace);

    return patchAsync(url, {selected_ids: ids, action: this.mutationKey});
  },

  runOptimisticUpdate(data, payload) {
    const ids = payload.bundles.map((bundle) => bundle.id);
    return {
      ...data,
      items: data.items.filter((bundle) => !bundle.id || !ids.includes(bundle.id)),
    };
  },
};

const reorderMutation: ListMutation<RuleListData, ReorderMutationAction, readonly RuleBundleModelStrict[]> = {
  mutationKey: RuleBundleMutationsEnum.REORDER,

  async runSideEffect(payload) {
    const {company, bundle} = payload;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return patchApiAsync(
      ApiRuleBundles,
      `${company.url}/namespaces/${bundle.namespace}/rule_bundles/${bundle.id}/reorder`,
      {sort_order: payload.newOrder},
    ) as Promise<ReadonlyArray<RuleBundleModelStrict>>;
  },

  runOptimisticUpdate(data, payload) {
    return {
      ...data,
      items: data.items.map((item) => runReorderOptimisticUpdate(item, payload)),
    };
  },

  runCacheReconciliation(data, result) {
    return {
      ...data,
      items: data.items.map((bundle) => {
        const updatedBundle = result.find((apiBundle) => apiBundle.id === bundle.id);

        if (!updatedBundle) {
          return bundle;
        }

        return {
          ...updatedBundle,
          rules: bundle.rules,
        };
      }),
    };
  },
};

function isAffectedByReorderMutation(
  bundle: RuleBundleModelStrict,
  payload: ReorderMutationAction['payload'],
): boolean {
  const minPosition = Math.min(payload.previousOrder, payload.newOrder);
  const maxPosition = Math.max(payload.previousOrder, payload.newOrder);

  if (maxPosition === minPosition) {
    return false;
  }

  if (bundle.id && payload.bundle.id === bundle.id) {
    return true;
  }

  if (!bundle.sortOrder) {
    return false;
  }

  return (
    bundle.sortOrder >= minPosition &&
    bundle.sortOrder <= maxPosition &&
    bundle.sortOrder !== payload.previousOrder
  );
}

function runReorderOptimisticUpdate(
  bundle: RuleBundleModelStrict,
  payload: ReorderMutationAction['payload'],
): RuleBundleModelStrict {
  if (!isAffectedByReorderMutation(bundle, payload)) {
    return bundle;
  }

  if (bundle.id === payload.bundle.id) {
    return {
      ...bundle,
      sortOrder: payload.newOrder,
    };
  }

  if (!bundle.sortOrder) {
    return bundle;
  }

  const movement = payload.previousOrder > payload.newOrder ? 1 : -1;

  return {
    ...bundle,
    sortOrder: bundle.sortOrder + movement,
  };
}

function getChangeActiveStateFunction(action: RuleBundleChangeActiveStatusActionsEnum) {
  switch (action) {
    case RuleBundleChangeActiveStatusActionsEnum.SET_ACTIVE:
      return (rule: RuleBaseModelStrict) => ({...rule, active: true});

    case RuleBundleChangeActiveStatusActionsEnum.SET_INACTIVE:
      return (rule: RuleBaseModelStrict) => ({...rule, active: false});

    case RuleBundleChangeActiveStatusActionsEnum.TOGGLE_ACTIVE:
      return (rule: RuleBaseModelStrict) => ({...rule, active: !rule.active});

    default:
      throw new UnreachableError(action);
  }
}

export const ruleListMutations = [changeActiveStatusMutation, deleteMutation, reorderMutation] as const;
